import React,{useEffect,useRef,useState} from 'react';
import SaasShell from './components/SaasShell';
import CsvAssessmentBuilder from './components/CsvAssessmentBuilder';
import RestSourceSetup from './components/RestSourceSetup';
import JiraRestBuilder from './components/JiraRestBuilder';
import ServiceNowRestBuilder from './components/ServiceNowRestBuilder';
import WebProcessAdvisor from './components/WebProcessAdvisor';
import Overview from './pages/Overview';
import Models from './pages/Models';
import Settings from './pages/Settings';
import WorkspaceManagement from './pages/WorkspaceManagement';
import ConnectionsPanel from './components/ConnectionsPanel';
import AccessBilling from './pages/AccessBilling';
import AuthPage from './pages/AuthPage';
import { apiHealth, authApi } from './services/apiClient';
import { modelRepository } from './services/modelRepository';
import { configureAIModel } from '@advisor-core/lib/aiModelConfig';

const FALLBACK_AI_CONFIG={provider:'gemini',model:'gemini-3.1-pro-preview',temperature:0.3,label:'Managed AI'};
configureAIModel(FALLBACK_AI_CONFIG);

const INVITE_STORAGE_KEY='process-advisor-pending-invite';
function captureInvite(){
 try{
  const hash=window.location.hash||'';
  const match=hash.match(/^#invite=([^&]+)/);
  if(match){const token=decodeURIComponent(match[1]);if(token.length>=30&&token.length<=256)sessionStorage.setItem(INVITE_STORAGE_KEY,token);history.replaceState(null,'',window.location.pathname+window.location.search);}
  return Boolean(sessionStorage.getItem(INVITE_STORAGE_KEY));
 }catch{return false;}
}
export default function App(){
 const [route,setRoute]=useState(()=>{try{return captureInvite()?'workspace':new URLSearchParams(window.location.search).has('billing')?'billing':'overview';}catch(_){return 'overview';}}); const [models,setModels]=useState([]); const [selected,setSelected]=useState(null); const [toast,setToast]=useState(''); const [advisorSection,setAdvisorSection]=useState('evidence'); const [newSourceType,setNewSourceType]=useState(null); const [restConnection,setRestConnection]=useState(null);
 const [boot,setBoot]=useState('loading'); const [identity,setIdentity]=useState(null); const [health,setHealth]=useState(null); const [authMode,setAuthMode]=useState('login'); const [bootError,setBootError]=useState(''); const [accessStatus,setAccessStatus]=useState(null); const [assessmentDialog,setAssessmentDialog]=useState(null); const [assessmentDialogBusy,setAssessmentDialogBusy]=useState(false);
 const openRequestRef=useRef(0);
 const [openingModel,setOpeningModel]=useState(null);
 const refresh=async()=>setModels(await modelRepository.listModels());
 const refreshAccess=async()=>{const next=await modelRepository.getAccessStatus();setAccessStatus(next);return next;};
 const syncAIConfig=async()=>{const cfg=await modelRepository.getAISettings();configureAIModel({provider:cfg.provider,model:cfg.model,temperature:0.3,label:`${cfg.mode==='customer'?'Customer':'Managed'} · ${cfg.provider} · ${cfg.model}`});return cfg;};
 useEffect(()=>{(async()=>{const h=await apiHealth();setHealth(h);if(!h){setBootError('Process Advisor API is unavailable. Check the production API connection and try again.');setBoot('error');return;}try{const s=await authApi.session();setIdentity(s.identity);await Promise.all([refresh(),refreshAccess(),syncAIConfig()]);if(captureInvite())setRoute('workspace');setBoot('ready');}catch(e){if(e.status===401){setBoot('auth');}else{setBootError(e.message||'Unable to start Process Advisor');setBoot('error');}}})();},[]);
 useEffect(()=>{if(!toast)return;const id=setTimeout(()=>setToast(''),2600);return()=>clearTimeout(id);},[toast]);
 useEffect(()=>{const onFocus=()=>refreshAccess().catch(()=>{});window.addEventListener('focus',onFocus);return()=>window.removeEventListener('focus',onFocus);},[]);
 const newModel=()=>{setSelected(null);setNewSourceType(null);setRestConnection(null);setRoute('new')};
 const newCsvModel=()=>{setSelected(null);setNewSourceType(null);setRestConnection(null);setRoute('new-csv')};
 const hydrate=async model=>model?.id ? (await modelRepository.getModel(model.id) || model) : model;
 const open=async model=>{
   const request=++openRequestRef.current;
   setOpeningModel(model);
   setSelected(null);
   setAdvisorSection('evidence');
   setRoute('advisor-loading');
   try{
     const full=await hydrate(model);
     if(request!==openRequestRef.current)return;
     setSelected(full);
     setOpeningModel(null);
     if (!full?.latestRuntime?.canonicalDataset && Number(full?.summary?.processedCases || full?.summary?.cases || 0) > 0) {
       setToast('Processed data was not saved. Choose Process data and keep Save processed data selected to reopen this model.');
       setRoute('models');
     } else {
       setRoute(full?.latestRuntime?.canonicalDataset?'advisor':'refresh');
     }
   }catch(error){
     if(request!==openRequestRef.current)return;
     setOpeningModel(null);
     setRoute('models');
     setToast(error?.message||'Unable to open model');
   }
 };
 const openAssessment=async model=>{const full=await hydrate(model);setSelected(full);setAdvisorSection('assessment');setRoute(full?.latestRuntime?.canonicalDataset?'advisor':'refresh')};
 const save=async data=>{const saved=await modelRepository.saveModel(data);await refresh();setSelected(saved);setToast('Model saved');return saved;};
 const runProcessAdvisor=async data=>{
   const retainProcessData=Boolean(data?.persistence?.retainProcessData ?? data?.persistence?.storageConsent ?? data?.source?.retainProcessData ?? data?.source?.storageConsent ?? false);
   let runtime=data?.latestRuntime||data?.runtime||null;

   // Final client-side safety boundary for the free tier. The API/extractor is the
   // primary enforcement point, but Evidence/Advisor must never receive more than
   // the authorized free subset even if an older API process or cached response is
   // still running. Preserve case order and discard events for cases outside it.
   const declaredAccess=String(data?.accessSource||data?.entitlement?.source||data?.source?.accessSource||data?.summary?.accessSource||'').toLowerCase();
   const declaredLimit=Number(data?.entitlement?.processingLimit||data?.summary?.processingLimit||0);
   const freeLimit=declaredAccess==='free' ? Math.max(1,Math.min(declaredLimit||250,250)) : 0;
   if(freeLimit && runtime?.canonicalDataset?.cases?.length>freeLimit){
     const dataset=runtime.canonicalDataset;
     const cases=dataset.cases.slice(0,freeLimit);
     const ids=new Set(cases.map(c=>String(c.id)));
     const events=(dataset.events||[]).filter(e=>ids.has(String(e.caseId)));
     runtime={
       ...runtime,
       canonicalDataset:{
         ...dataset,
         cases,
         events,
         metadata:{
           ...(dataset.metadata||{}),
           ingestion:{
             ...(dataset.metadata?.ingestion||{}),
             processedCaseCount:cases.length,
             processingLimit:freeLimit,
             partialProcessing:true,
             accessSource:'free',
           },
         },
       },
       validation:{
         ...(runtime.validation||{}),
         summary:{
           ...(runtime.validation?.summary||{}),
           cases:cases.length,
           events:events.length,
           processedCases:cases.length,
           processingLimit:freeLimit,
           partialProcessing:true,
         },
       },
     };
     data={
       ...data,
       latestRuntime:runtime,
       runtime,
       summary:{
         ...(data.summary||{}),
         cases:cases.length,
         events:events.length,
         processedCases:cases.length,
         processingLimit:freeLimit,
         partialProcessing:true,
         accessSource:'free',
       },
     };
   }

   const processedCount=Number(runtime?.canonicalDataset?.cases?.length||data?.summary?.processedCases||data?.summary?.cases||0);
   const eventCount=Number(runtime?.canonicalDataset?.events?.length||data?.summary?.events||0);
   try{
     // Jira pre-authorizes before extraction. CSV and future local imports can
     // authorize here immediately before the Process Advisor run.
     let workingData=data;
     let authorization=data?.entitlement?.allowed
       ? data.entitlement
       : await modelRepository.authorizeAssessment({
           modelId:data?.id||null,
           modelName:data?.name||'Process Advisor model',
           caseCount:processedCount,
           eventCount,
           processedUnitCount:processedCount,
           sourceConnectionId:data?.source?.connectionId||null,
           storageRequested:retainProcessData,
         });

     const effectiveRetention=Boolean(retainProcessData && authorization?.storageAllowed);
     const payload={
       ...workingData,
       source:{
         ...(data?.source||{}),
         retainProcessData:effectiveRetention,
         storageConsent:effectiveRetention,
         accessSource:data?.accessSource||authorization?.source||data?.source?.accessSource||null,
       },
       persistence:{
         ...(data?.persistence||{}),
         modelDefinitionSaved:true,
         retainProcessData:effectiveRetention,
         storageConsent:effectiveRetention,
         retentionMode:effectiveRetention?'workspace-data':'definition-only',
       },
       accessAuthorizationId:data?.accessAuthorizationId||authorization?.authorizationId||null,
       accessSource:data?.accessSource||authorization?.source||null,
       entitlement:{...authorization,processedUnitCount:processedCount},
       summary:{
         ...(data?.summary||{}),
         accessSource:data?.accessSource||authorization?.source||data?.summary?.accessSource||null,
       },
       lastRunAt:new Date().toISOString(),
       status:'ready',
       assessmentStatus:'not-run',
     };

     // Processing a source model is NOT an assessment. Save/update the model and
     // optional canonical snapshot, but do not create assessment history here.
     const saved=await modelRepository.saveModel(payload);
     await refresh();

     const liveModel={
       ...payload,
       ...saved,
       latestRuntime:runtime,
       runtime,
       assessmentStatus:'not-run',
     };

     if(['assessment','assessment_credit'].includes(authorization?.source)){
       setToast(`Assessment active${authorization.expiresAt?` until ${new Date(authorization.expiresAt).toLocaleDateString()}`:''} · model ready`);
     }else if(authorization?.source==='free'){
       setToast(`Free processing allowance used · ${processedCount.toLocaleString()} processed`);
     }else{
       setToast(effectiveRetention?'Model saved · process data retained':'Model saved · process data not retained');
     }
     await refreshAccess().catch(()=>{});
     setSelected(liveModel);setAdvisorSection('evidence');setRoute('advisor');
   }catch(e){
     console.error(e);
     await refresh().catch(()=>{});
     if(e?.status===402){
       setToast(e.message||'Assessment access is required');
       await refreshAccess().catch(()=>{});
       setRoute('billing');
       return;
     }
     setToast(e?.message||'Unable to run Process Advisor');
   }
 };
 const remove=async model=>{if(!window.confirm(`Delete model "${model.name}" and its saved model data?`))return;await modelRepository.deleteModel(model.id);await refresh();setToast('Model deleted');};
 const duplicate=async model=>{const copy=await modelRepository.duplicateModel(model.id);await refresh();setToast(`Created ${copy?.name || 'model copy'}`);return copy;};
 const editModel=async(model,patch)=>{const saved=await modelRepository.saveModel({...model,...patch});await refresh();setSelected(saved);setToast('Model details updated');return saved;};
 const performAssessmentActivation=async full=>{
   try{
     const configuredCases=Number(full?.summary?.configuredCases||full?.summary?.requestedCases||full?.summary?.cases||0);
     const result=await modelRepository.activateAssessment(full.id,{caseCount:configuredCases,eventCount:Number(full?.summary?.events||0),storageRequested:Boolean(full?.persistence?.retainProcessData)});
     await Promise.all([refresh(),refreshAccess()]);
     const hydrated=await hydrate(full);
     if(selected?.id===full.id)setSelected(hydrated);
     setToast(result?.source==='subscription'?'Covered by active subscription':`Assessment activated${result?.expiresAt?` until ${new Date(result.expiresAt).toLocaleDateString()}`:''}`);
     return result;
   }catch(e){
     await refreshAccess().catch(()=>{});
     if(e?.status===402){setToast(e.message||'No unused Process Advisor Assessments are available');return null;}
     throw e;
   }
 };
 const activateAssessment=async model=>{
   const full=await hydrate(model);
   if(full?.currentAccess?.source==='subscription'){setToast('This model is already covered by its source subscription');return full;}
   if(full?.currentAccess?.source==='assessment'){setToast('An assessment is already active for this model');return full;}
   return await new Promise(resolve=>setAssessmentDialog({model:full,resolve}));
 };
 const confirmAssessmentActivation=async()=>{
   if(!assessmentDialog?.model||assessmentDialogBusy)return;
   setAssessmentDialogBusy(true);
   try{
     const result=await performAssessmentActivation(assessmentDialog.model);
     assessmentDialog.resolve?.(result);
     setAssessmentDialog(null);
   }catch(e){
     console.error(e);
     setToast(e?.message||'Unable to activate assessment');
   }finally{setAssessmentDialogBusy(false);}
 };
 const cancelAssessmentActivation=()=>{assessmentDialog?.resolve?.(null);setAssessmentDialog(null);};
 const refreshModel=async model=>{const full=await hydrate(model);setSelected(full);setAdvisorSection('evidence');setRoute('refresh');};
 const processModel=async model=>{const full=await hydrate(model);setSelected(full);setAdvisorSection('evidence');setRoute('process');};
 const activateAndProcess=async model=>{
   try{
     const result=await activateAssessment(model);
     if(!result)return null;
     const full=await hydrate(model);
     setSelected(full);setAdvisorSection('evidence');setRoute('process');
     return result;
   }catch(e){
     console.error(e);
     setToast(e?.message||'Unable to activate assessment');
     return null;
   }
 };

 const reset=async()=>{if(!window.confirm('Clear all models and saved model data in the current workspace?'))return;await modelRepository.clearAll();await refresh();setToast('Workspace model data cleared');};
 const closeAdvisor=()=>{++openRequestRef.current;setOpeningModel(null);setRoute('models');setSelected(null);setAdvisorSection('evidence');refresh();};
 const goRoute=r=>{++openRequestRef.current;setOpeningModel(null);setSelected(null);setAdvisorSection('evidence');setRoute(r);if(r==='models')Promise.all([refresh(),refreshAccess()]).catch(()=>{});if(r==='billing')refreshAccess().catch(()=>{});};
 const goAdvisorSection=section=>{if(!selected)return;setAdvisorSection(section);setRoute('advisor');};

 useEffect(()=>{const handler=async()=>{
   if(!selected)return;
   const retainProcessData=Boolean(selected?.persistence?.retainProcessData ?? selected?.persistence?.storageConsent ?? selected?.source?.retainProcessData ?? selected?.source?.storageConsent ?? false);
   const payload=retainProcessData
     ? selected
     : {...selected,runtime:undefined,latestRuntime:undefined,source:{...(selected.source||{}),retainProcessData:false,storageConsent:false},persistence:{...(selected.persistence||{}),modelDefinitionSaved:true,retainProcessData:false,storageConsent:false}};
   const saved=await modelRepository.saveModel(payload);setSelected({...selected,...saved,latestRuntime:selected.latestRuntime});await refresh();setToast(retainProcessData?'Model saved':'Model definition saved');
 };window.addEventListener('process-advisor-web-save-request',handler);return()=>window.removeEventListener('process-advisor-web-save-request',handler);},[selected]);

 let page=null;
 if(route==='advisor-loading') page=<div className="boot-screen" role="status" aria-live="polite"><div className="boot-spinner"/><b>Opening Process Advisor…</b><span>{openingModel?.name||'Loading model'}</span></div>;
 else if(route==='advisor'&&selected) page=<WebProcessAdvisor assessment={selected} section={advisorSection} onSectionChange={setAdvisorSection} accessStatus={accessStatus} onActivateAndProcess={activateAndProcess} onBuyAssessment={()=>{setRoute('billing');refreshAccess().catch(()=>{});}}/>;
 else if(route==='overview') page=<Overview models={models} onNew={newModel} onOpen={open} onModels={()=>setRoute('models')} onConnections={()=>setRoute('connections')}/>;
 else if(route==='models') page=<Models models={models} accessStatus={accessStatus} onOpen={open} onNew={newModel} onDelete={remove} onDuplicate={duplicate} onRefresh={refresh} onRefreshModel={refreshModel} onProcessModel={processModel} onEditModel={editModel} onActivateAssessment={activateAssessment}/>;
 else if(route==='new-csv') page=<CsvAssessmentBuilder startWithCsv repository={modelRepository} onSave={save} onRunProcessAdvisor={runProcessAdvisor} accessStatus={accessStatus}/>;
 else if(route==='new') page=<CsvAssessmentBuilder repository={modelRepository} onSave={save} onRunProcessAdvisor={runProcessAdvisor} accessStatus={accessStatus} onSelectRestSource={type=>{setNewSourceType(type);setRoute('new-rest');}}/>;
 else if(route==='new-rest'&&newSourceType) page=<RestSourceSetup sourceType={newSourceType} repository={modelRepository} onBack={()=>{setNewSourceType(null);setRestConnection(null);setRoute('new');}} onManageConnections={()=>setRoute('connections')} onSelectConnection={(id,connection)=>{setRestConnection(connection||{id});if(newSourceType==='jira-rest')setRoute('jira-build');else if(newSourceType==='servicenow-rest')setRoute('servicenow-build');}}/>;
 else if(route==='jira-build'&&restConnection?.id) page=<JiraRestBuilder repository={modelRepository} connectionId={restConnection.id} connection={restConnection} accessStatus={accessStatus} onBack={()=>setRoute('new-rest')} onRunProcessAdvisor={runProcessAdvisor}/>;
 else if(route==='servicenow-build'&&restConnection?.id) page=<ServiceNowRestBuilder repository={modelRepository} connectionId={restConnection.id} connection={restConnection} accessStatus={accessStatus} onBack={()=>setRoute('new-rest')} onRunProcessAdvisor={runProcessAdvisor}/>;
 else if(route==='refresh'&&selected?.sourceType==='jira-rest') page=<JiraRestBuilder repository={modelRepository} connectionId={selected?.source?.connectionId} connection={null} initial={selected} accessStatus={accessStatus} onBack={()=>setRoute('models')} onRunProcessAdvisor={runProcessAdvisor}/>;
 else if(route==='refresh'&&selected?.sourceType==='servicenow-rest') page=<ServiceNowRestBuilder repository={modelRepository} connectionId={selected?.source?.connectionId} connection={null} initial={selected} accessStatus={accessStatus} onBack={()=>setRoute('models')} onRunProcessAdvisor={runProcessAdvisor}/>;
 else if(route==='process'&&selected?.sourceType==='jira-rest') page=<JiraRestBuilder repository={modelRepository} connectionId={selected?.source?.connectionId} connection={null} initial={selected} initialStage="run" accessStatus={accessStatus} onBack={()=>setRoute('models')} onRunProcessAdvisor={runProcessAdvisor}/>;
 else if(route==='process'&&selected?.sourceType==='servicenow-rest') page=<ServiceNowRestBuilder repository={modelRepository} connectionId={selected?.source?.connectionId} connection={null} initial={selected} initialStage={6} accessStatus={accessStatus} onBack={()=>setRoute('models')} onRunProcessAdvisor={runProcessAdvisor}/>;
 else if(route==='refresh'||route==='process') page=<CsvAssessmentBuilder repository={modelRepository} onSave={save} onRunProcessAdvisor={runProcessAdvisor} initial={{...selected,runtime:selected?.latestRuntime}} refreshSource={true} accessStatus={accessStatus}/>;
 else if(route==='billing') page=<AccessBilling repository={modelRepository} status={accessStatus} onStatusChanged={setAccessStatus}/>;
 else if(route==='connections') page=<><div className="page-head"><div><div className="eyebrow">Data sources</div><h1>Connections</h1><p>Manage connections to your source systems.</p></div></div><section className="panel"><div className="panel-body connections-panel-body"><ConnectionsPanel repository={modelRepository} onAddCsv={newCsvModel}/></div></section></>;
 else if(route==='workspace') page=<WorkspaceManagement identity={identity} onIdentityChanged={async next=>{setIdentity(next);setSelected(null);setModels([]);setAccessStatus(null);setRoute('overview');await Promise.all([refresh(),refreshAccess(),syncAIConfig()]);}}/>;
 else if(route==='settings') page=<Settings repository={modelRepository} onAIConfigChanged={syncAIConfig} identity={identity}/>;

 if(boot==='loading') return <div className="boot-screen"><div className="boot-spinner"/><b>Starting Process Advisor…</b></div>;
 if(boot==='error') return <div className="service-error"><div><h1>Process Advisor is unavailable</h1><p>{bootError}</p><button onClick={()=>window.location.reload()}>Retry</button></div></div>;
 if(boot==='auth') return <AuthPage mode={authMode} onMode={setAuthMode} health={health} onAuthenticated={async i=>{setIdentity(i);await Promise.all([refresh(),refreshAccess(),syncAIConfig()]);if(captureInvite())setRoute('workspace');setBoot('ready');}}/>;
 const signOut=async()=>{try{await authApi.logout();}finally{setModels([]);setSelected(null);setIdentity(null);setAccessStatus(null);setRoute('overview');setBoot('auth');}};
 return <SaasShell route={(route==='advisor-loading')?'advisor':(route==='refresh'||route==='process')?'models':(route==='new-csv'||route==='new-rest'||route==='jira-build'||route==='servicenow-build')?'new':route} onRoute={goRoute} activeModel={route==='advisor-loading'?openingModel:route==='advisor'?selected:null} advisorSection={advisorSection} onAdvisorSection={goAdvisorSection} onCloseModel={closeAdvisor} identity={identity} onSignOut={signOut} accessStatus={accessStatus}>
   {toast&&<div className="toast">{toast}</div>}
   {page}

 </SaasShell>;
}
